import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash, PaperPlaneRight, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ROLE_META, STATUS_META, ACTIVITY_META, ACTIVITY_TYPES } from "@/lib/meta";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "rounded-none bg-secondary border-border focus-visible:ring-primary";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function ContactDrawer({ contact, company, open, onClose, onEmail, emailReady }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ type: "call", subject: "", body: "", direction: "out" });
  const [compose, setCompose] = useState(null);

  const { data: activities = [] } = useQuery({
    queryKey: ["activities", contact?.id],
    queryFn: async () => (await api.get(`/admin/users/${contact.id}/activities`)).data,
    enabled: !!contact?.id && open,
  });

  const { data: sigs = [] } = useQuery({
    queryKey: ["contact-signatures", contact?.id],
    queryFn: async () => (await api.get(`/admin/users/${contact.id}/signatures`)).data,
    enabled: !!contact?.id && open,
  });

  const addMut = useMutation({
    mutationFn: async (body) => api.post(`/admin/users/${contact.id}/activities`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["activities", contact.id] }); setForm({ type: "call", subject: "", body: "", direction: "out" }); toast.success("Touchpoint logged"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/activities/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["activities", contact.id] }); toast.success("Deleted"); },
  });
  const sendMut = useMutation({
    mutationFn: async (body) => api.post("/admin/gmail/send", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["activities", contact.id] }); setCompose(null); toast.success("Email sent"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });
  const syncMut = useMutation({
    mutationFn: async () => api.post(`/admin/gmail/sync/${contact.id}`),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["activities", contact.id] }); toast.success(`Imported ${res.data.imported} of ${res.data.found} emails`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });

  const [aiBusy, setAiBusy] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiReplies, setAiReplies] = useState(null);
  const aiDraft = async () => {
    setAiBusy("draft");
    try { const { data } = await api.post("/admin/ai/draft-email", { contact_id: contact.id }); setCompose({ subject: data.subject, body: data.body }); toast.success("Draft ready — review before sending"); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setAiBusy(null); }
  };
  const aiSummarize = async () => {
    setAiBusy("summary"); setAiSummary(null);
    try { const { data } = await api.post("/admin/ai/summarize-contact", { contact_id: contact.id }); setAiSummary(data.summary); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI summary failed"); }
    finally { setAiBusy(null); }
  };
  const aiReply = async () => {
    setAiBusy("replies"); setAiReplies(null);
    try { const { data } = await api.post("/admin/ai/suggest-replies", { contact_id: contact.id }); setAiReplies(data.replies); }
    catch (e) { toast.error(e?.response?.data?.detail || "No inbound email to reply to"); }
    finally { setAiBusy(null); }
  };

  if (!contact) return null;
  const role = ROLE_META[contact.role] || ROLE_META.lead;
  const status = STATUS_META[contact.status] || STATUS_META.active;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="rounded-none border-border bg-card w-full sm:max-w-xl overflow-y-auto p-0" data-testid="contact-drawer">
        <SheetHeader className="p-6 border-b border-border text-left">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary">Contact</span>
          <SheetTitle className="font-head text-2xl font-normal">{contact.name}</SheetTitle>
          <p className="font-mono text-xs text-muted-foreground">{contact.email}</p>
          <div className="flex flex-wrap gap-2 pt-2">
            <span className={`px-2 py-0.5 border text-[10px] font-mono tracking-widest uppercase ${role.color}`}>{role.label}</span>
            <span className={`px-2 py-0.5 border text-[10px] font-mono tracking-widest uppercase ${status.color}`}>{status.label}</span>
            {contact.source && <span className="px-2 py-0.5 border border-border text-[10px] font-mono tracking-widest uppercase text-muted-foreground">{contact.source}</span>}
          </div>
        </SheetHeader>

        <div className="p-6 space-y-6">
          {/* Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Phone</p><p className="font-light">{contact.phone || "—"}</p></div>
            <div><p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Company</p><p className="font-light">{contact.company || "—"}</p></div>
          </div>
          {contact.notes && <div><p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Notes</p><p className="font-light text-sm mt-1">{contact.notes}</p></div>}

          {/* Company / Brreg */}
          {company && (
            <div className="border border-border p-4 bg-secondary/40" data-testid="drawer-company">
              <p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-2">Company · Brreg</p>
              <p className="font-head text-lg">{company.legal_name || company.name}</p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs font-light text-muted-foreground">
                {company.org_number && <span>Org: <span className="font-mono text-foreground">{company.org_number}</span></span>}
                {company.status && <span>Status: {company.status}</span>}
                {company.org_form && <span>Form: {company.org_form}</span>}
                {company.industry_text && <span className="col-span-2">NACE {company.industry_code}: {company.industry_text}</span>}
                {company.address && <span className="col-span-2">{company.address}</span>}
              </div>
            </div>
          )}

          {/* 3LA scores */}
          {contact.tla && (
            <div className="border border-border p-4" data-testid="drawer-tla">
              <p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-2">3LA report card</p>
              <div className="grid grid-cols-3 gap-3">
                {[["l1", "L1 Human"], ["l2", "L2 Search"], ["l3", "L3 AI"]].map(([k, lbl]) => (
                  <div key={k}>
                    <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{lbl}</span>
                    <p className="font-head text-2xl font-light">{contact.tla[k] ?? "—"}</p>
                  </div>
                ))}
              </div>
              {contact.tla.note && <p className="text-xs text-muted-foreground font-light mt-2">{contact.tla.note}</p>}
            </div>
          )}

          {/* Signed agreements */}
          {sigs.length > 0 && (
            <div className="border border-border p-4" data-testid="drawer-signatures">
              <p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-2">Signed agreements</p>
              <div className="space-y-2">
                {sigs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`drawer-signature-${s.id}`}>
                    <span className="font-light truncate">{s.resource_title}</span>
                    <span className="font-mono text-[10px] text-muted-foreground shrink-0">{fmt(s.signed_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Send / sync email */}
          <div className="flex gap-3">
            <Button
              data-testid="drawer-send-email"
              onClick={() => (emailReady ? setCompose({ subject: "", body: "" }) : onEmail?.(contact))}
              className="flex-1 rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide"
            >
              <PaperPlaneRight size={16} className="mr-2" /> {emailReady ? "Send email" : "Send email (connect Gmail)"}
            </Button>
            {emailReady && (
              <Button data-testid="drawer-sync-email" onClick={() => syncMut.mutate()} disabled={syncMut.isPending} className="rounded-none bg-secondary border border-border text-foreground hover:bg-accent font-mono text-xs tracking-wide">
                <ArrowsClockwise size={16} className={`mr-2 ${syncMut.isPending ? "animate-spin" : ""}`} /> Sync inbox
              </Button>
            )}
          </div>

          {/* AI assistant (Claude) */}
          <div className="border border-primary/30 bg-primary/[0.03] p-4 space-y-3" data-testid="drawer-ai">
            <div className="flex items-center gap-2">
              <Sparkle size={15} className="text-primary" />
              <p className="font-mono text-[10px] tracking-widest uppercase text-primary">Claude assistant</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="ai-draft-email" onClick={aiDraft} disabled={aiBusy} className="rounded-none bg-secondary border border-border text-foreground hover:bg-accent font-mono text-xs tracking-wide">
                {aiBusy === "draft" ? "Drafting…" : "Draft email"}
              </Button>
              <Button data-testid="ai-summarize" onClick={aiSummarize} disabled={aiBusy} className="rounded-none bg-secondary border border-border text-foreground hover:bg-accent font-mono text-xs tracking-wide">
                {aiBusy === "summary" ? "Summarizing…" : "Summarize"}
              </Button>
              <Button data-testid="ai-suggest-replies" onClick={aiReply} disabled={aiBusy} className="rounded-none bg-secondary border border-border text-foreground hover:bg-accent font-mono text-xs tracking-wide">
                {aiBusy === "replies" ? "Thinking…" : "Suggest replies"}
              </Button>
            </div>
            {aiSummary && (
              <div className="text-sm text-foreground/90 font-light whitespace-pre-wrap border-t border-border pt-3" data-testid="ai-summary-result">{aiSummary}</div>
            )}
            {aiReplies && (
              <div className="space-y-2 border-t border-border pt-3" data-testid="ai-replies-result">
                {aiReplies.map((r, i) => (
                  <button key={i} data-testid={`ai-reply-${i}`} onClick={() => { setCompose({ subject: `Re: ${contact.name || ""}`, body: r }); setAiReplies(null); }} className="block w-full text-left text-sm font-light border border-border p-2 hover:border-primary transition-colors whitespace-pre-wrap">
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Add touchpoint */}
          <div className="border border-border p-4 space-y-3" data-testid="add-touchpoint-form">
            <p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Log a touchpoint</p>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="touchpoint-type" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">{ACTIVITY_TYPES.map((t) => <SelectItem key={t} value={t} className="rounded-none">{ACTIVITY_META[t].label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger data-testid="touchpoint-direction" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="out" className="rounded-none">Outbound</SelectItem>
                  <SelectItem value="in" className="rounded-none">Inbound</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input data-testid="touchpoint-subject" className={inputCls} placeholder="Subject / summary" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            <Textarea data-testid="touchpoint-body" rows={2} className={inputCls} placeholder="Details (optional)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <Button
              data-testid="touchpoint-save"
              disabled={!form.subject || addMut.isPending}
              onClick={() => addMut.mutate(form)}
              className="rounded-none bg-foreground text-background hover:bg-foreground/90 font-mono text-xs tracking-wide"
            >Add touchpoint</Button>
          </div>

          {/* Timeline */}
          <div>
            <p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-3">Activity log ({activities.length})</p>
            {activities.length === 0 ? (
              <p className="text-sm text-muted-foreground font-light" data-testid="timeline-empty">No touchpoints logged yet.</p>
            ) : (
              <div className="space-y-0 border-l border-border ml-2">
                {activities.map((a) => {
                  const Icon = ACTIVITY_META[a.type]?.icon || NotePencilFallback;
                  return (
                    <div key={a.id} className="relative pl-6 pb-5 group" data-testid={`activity-${a.id}`}>
                      <div className="absolute -left-[9px] top-0.5 w-4 h-4 bg-card border border-primary flex items-center justify-center">
                        <Icon size={10} className="text-primary" />
                      </div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            {a.subject}
                            {a.direction && <span className="ml-2 font-mono text-[9px] tracking-widest uppercase text-muted-foreground">{a.direction === "out" ? "↗ out" : "↙ in"}</span>}
                          </p>
                          {a.body && <p className="text-xs text-muted-foreground font-light mt-0.5">{a.body}</p>}
                          <p className="font-mono text-[10px] text-muted-foreground mt-1">{ACTIVITY_META[a.type]?.label} · {fmt(a.occurred_at)}</p>
                        </div>
                        <button onClick={() => delMut.mutate(a.id)} data-testid={`activity-delete-${a.id}`} className="md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all shrink-0">
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <Dialog open={!!compose} onOpenChange={(o) => !o && setCompose(null)}>
          <DialogContent className="rounded-none border-border bg-card" data-testid="compose-dialog">
            <DialogHeader>
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary text-left">New email</span>
              <DialogTitle className="font-head text-xl font-normal text-left">To {contact.email}</DialogTitle>
              <DialogDescription className="sr-only">Compose an email to this contact.</DialogDescription>
            </DialogHeader>
            {compose && (
              <div className="space-y-3">
                <Input data-testid="compose-subject" className={inputCls} placeholder="Subject" value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} />
                <Textarea data-testid="compose-body" rows={7} className={inputCls} placeholder="Write your message…" value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} />
              </div>
            )}
            <DialogFooter>
              <Button
                data-testid="compose-send"
                disabled={!compose?.subject || !compose?.body || sendMut.isPending}
                onClick={() => sendMut.mutate({ to: contact.email, subject: compose.subject, body: compose.body, contact_id: contact.id })}
                className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide"
              >
                <PaperPlaneRight size={16} className="mr-2" /> {sendMut.isPending ? "Sending…" : "Send"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

function NotePencilFallback() { return null; }
